import { createContext, useContext, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { AsyncResult } from '../types/AsyncResult';
import { ChildrenOnly } from '../types/ChildrenOnly';
import { returnVoid } from '../util/returnVoid';

export enum LoginMethodType {
  APPLE = 'apple',
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
  MYTWC = 'myTWC',
}

export type CorporateGroupIdentityLoginMethod = {
  displayName: string;
  type: string | null;
  iconPath?: string;
  fields?: {
    idp: string;
  };
};

export const TWCMLoginMethods: CorporateGroupIdentityLoginMethod[] = [
  {
    displayName: 'myTWC',
    type: LoginMethodType.MYTWC,
    iconPath: 'public/images/tw_cross_transparent_20220113042251.svg',
  },
];

export const corporateGroupIdentityConfig: CorporateGroupIdentityConfig[] = [
  {
    id: 'twcm-dev',
    name: 'myTWC Dev',
    loginMethods: TWCMLoginMethods,
    signupUrl: 'https://terrywhitechemmart.com.au/join/my-twc',
  },
  {
    id: 'twcm-live',
    name: 'myTWC',
    loginMethods: TWCMLoginMethods,
    signupUrl: 'https://terrywhitechemmart.com.au/join/my-twc',
  },
];

export type CorporateGroupIdentityConfig = {
  id: string;
  name: string;
  loginMethods: CorporateGroupIdentityLoginMethod[];
  signupUrl: string;
};

export type CorporateGroupIdentityConfigResult = AsyncResult<CorporateGroupIdentityConfig>;

export const CorporateGroupIdentityConfigContext = createContext<CorporateGroupIdentityConfigResult>(
  {
    loading: true,
    refetch: returnVoid,
  },
);

export const CorporateGroupIdentityConfigProvider = (props: ChildrenOnly) => {
  const { whiteLabelProps } = useTheme();
  const identityProvider = whiteLabelProps?.identityProvider;
  let whiteLabelLoading: boolean;

  if (whiteLabelProps) {
    whiteLabelLoading = whiteLabelProps.loading;
  } else {
    whiteLabelLoading = true;
  }

  const config: CorporateGroupIdentityConfigResult = useMemo(() => {
    let data;

    if (identityProvider) {
      data = corporateGroupIdentityConfig.find(config => config.id === identityProvider);
    }

    return {
      loading: whiteLabelLoading,
      data,
    };
  }, [identityProvider, whiteLabelLoading]);

  return (
    <CorporateGroupIdentityConfigContext.Provider value={config}>
      {props.children}
    </CorporateGroupIdentityConfigContext.Provider>
  );
};

export const useCorporateGroupIdentityConfig = () =>
  useContext(CorporateGroupIdentityConfigContext);
